import { useState, useEffect, useCallback } from 'react'
import { Tab } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import ChangeEmailForm from './ChangeEmailForm'
import ChangePasswordForm from './ChangePasswordForm'
import PreferencesTab from './PreferencesTab'

/**
 * SettingsPage Component
 *
 * Comprehensive user settings management with tabbed interface.
 * Provides sections for profile management, security settings, and preferences.
 *
 * Features:
 * - Email change with verification
 * - Password change with strength validation
 * - User preferences (placeholder for future expansion)
 * - Responsive mobile-first design
 * - Accessible tab navigation
 */
function SettingsPage() {
  const { t } = useTranslation(['profile', 'common'])
  const navigate = useNavigate()
  const { user } = useAuth()
  const [selectedTab, setSelectedTab] = useState(0)

  // Keyboard navigation for tabs
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setSelectedTab(prev => Math.max(0, prev - 1))
      } else if (e.key === 'ArrowRight') {
        setSelectedTab(prev => Math.min(2, prev + 1))
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Handle navigation back
  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  // Tab configuration
  const tabs = [
    {
      key: 'profile',
      label: t('profile:tabs.profile.label'),
      description: t('profile:tabs.profile.description'),
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      key: 'security',
      label: t('profile:tabs.security.label'),
      description: t('profile:tabs.security.description'),
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      key: 'preferences',
      label: t('profile:tabs.preferences.label'),
      description: t('profile:tabs.preferences.description'),
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Breadcrumb / Back Navigation */}
            <button
              onClick={handleBack}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
              aria-label={t('common:navigation.back')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 19l-7-7 7-7" />
              </svg>
              {t('common:navigation.back')}
            </button>

            {/* Page Title */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('profile:page.title')}
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  {t('profile:page.subtitle')}
                </p>
              </div>

              {/* User Info Badge */}
              <div className="hidden sm:flex items-center space-x-3 bg-gray-50 px-4 py-2 rounded-lg">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-medium">
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{user?.email}</p>
                  <p className="text-gray-500">{t('profile:page.memberSince', {
                    date: new Date(user?.created_at).toLocaleDateString()
                  })}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation and Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          {/* Tab List - Desktop */}
          <Tab.List className="hidden sm:flex space-x-1 bg-white p-1 rounded-lg border border-gray-200 shadow-sm mb-8">
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                className={({ selected }) =>
                  `flex-1 flex items-center justify-center space-x-3 py-3 px-4 text-sm font-medium rounded-md transition-all
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  ${selected
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`
                }
                aria-label={tab.label}
              >
                <span className="shrink-0">{tab.icon}</span>
                <div className="text-left">
                  <p className="font-medium">{tab.label}</p>
                  <p className="text-xs text-gray-500 hidden lg:block">{tab.description}</p>
                </div>
              </Tab>
            ))}
          </Tab.List>

          {/* Tab List - Mobile */}
          <Tab.List className="sm:hidden flex flex-col space-y-2 mb-6">
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                className={({ selected }) =>
                  `flex items-center space-x-3 w-full py-3 px-4 text-sm font-medium rounded-lg transition-all
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  ${selected
                    ? 'bg-primary-50 text-primary-700 border-2 border-primary-200'
                    : 'bg-white text-gray-600 border border-gray-200'
                  }`
                }
                aria-label={tab.label}
              >
                <span className="shrink-0">{tab.icon}</span>
                <div className="text-left flex-1">
                  <p className="font-medium">{tab.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{tab.description}</p>
                </div>
                {selectedTab === tabs.findIndex(t => t.key === tab.key) && (
                  <svg className="w-5 h-5 text-primary-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </Tab>
            ))}
          </Tab.List>

          {/* Tab Panels */}
          <Tab.Panels>
            {/* Profile Tab */}
            <Tab.Panel className="focus:outline-none">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t('profile:profile.title')}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {t('profile:profile.subtitle')}
                  </p>
                </div>
                <div className="p-6">
                  <ChangeEmailForm currentEmail={user?.email} />
                </div>
              </div>
            </Tab.Panel>

            {/* Security Tab */}
            <Tab.Panel className="focus:outline-none">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t('profile:security.title')}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {t('profile:security.subtitle')}
                  </p>
                </div>
                <div className="p-6">
                  <ChangePasswordForm />
                </div>
              </div>
            </Tab.Panel>

            {/* Preferences Tab */}
            <Tab.Panel className="focus:outline-none">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t('profile:preferences.title')}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {t('profile:preferences.subtitle')}
                  </p>
                </div>
                <div className="p-6">
                  <PreferencesTab />
                </div>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {t('profile:help.title')}
              </h3>
              <p className="mt-2 text-sm text-blue-700">
                {t('profile:help.description')}
              </p>
              <p className="mt-3">
                <a href="/contact" className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors">
                  {t('profile:help.contactSupport')} →
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage